import type {JSX} from 'preact';

import type {TranslateOptions} from '~/foundation/I18n/types';

import type {InstallmentsContextType} from '../context/InstallmentsContext';
import type {AvailableLoanType, ModalText, ModalType} from '../types';

import {getSplitPayModalType} from './getModalType';
import {convertPriceToNumber} from './price';

type InstallmentsProps = Pick<
  InstallmentsContextType,
  | 'countryCode'
  | 'eligible'
  | 'fullPrice'
  | 'loanTypes'
  | 'minIneligibleMessageType'
  | 'numberOfPaymentTerms'
  | 'showNewBuyerIncentive'
>;

type GetSplitPayModalTextProps = InstallmentsProps & {
  lendersLink: JSX.Element;
  priceRange?: {
    minPrice: string;
    maxPrice: string;
  };
};

export const getSplitPayModalText = ({
  countryCode,
  eligible,
  fullPrice,
  lendersLink,
  loanTypes,
  minIneligibleMessageType,
  numberOfPaymentTerms,
  priceRange,
  showNewBuyerIncentive,
}: GetSplitPayModalTextProps): ModalText => {
  const modalType = getSplitPayModalType({eligible, loanTypes});

  const ineligibleSubtitleKey = getIneligibleSubtitleKey({
    countryCode,
    fullPrice,
    priceRange,
    numberOfPaymentTerms,
    minIneligibleMessageType,
  });

  // Set the subtitle key based on the modal type
  const subTitleKey =
    getSubTitleKey({modalType, numberOfPaymentTerms, countryCode}) ??
    ineligibleSubtitleKey;

  // Set the legal copy keys based on the country code and modal type
  const legalCopyKeys = getLegalCopyKeys({
    countryCode,
    lendersLink,
    modalType,
    loanTypes,
    showNewBuyerIncentive,
  });

  return {
    subTitleKey,
    legalCopyKeys,
  };
};

interface GetSubTitleKeyProps {
  modalType: ModalType;
  numberOfPaymentTerms?: number;
  countryCode?: string;
}

const getSubTitleKey = ({
  modalType,
  numberOfPaymentTerms,
  countryCode,
}: GetSubTitleKeyProps) => {
  switch (countryCode) {
    case 'GB':
      return getSubTitleKeyGB({modalType});
    default:
      return getSubTitleKeyDefault({modalType, numberOfPaymentTerms});
  }
};

const getSubTitleKeyDefault = ({
  modalType,
  numberOfPaymentTerms,
}: GetSubTitleKeyProps) => {
  switch (modalType) {
    case 'adaptive': {
      return 'paymentTerms.splitPayContents.subtitle.interestAndSplitPay';
    }
    case 'split_pay': {
      if (numberOfPaymentTerms === 2) {
        return 'paymentTerms.splitPayContents.subtitle.splitPayOnly2';
      } else if (numberOfPaymentTerms === 1) {
        return 'paymentTerms.splitPayContents.subtitle.splitPayOnly30';
      } else {
        return 'paymentTerms.splitPayContents.subtitle.splitPayOnly';
      }
    }
    case 'interest_only': {
      return 'paymentTerms.splitPayContents.subtitle.interestOnly';
    }
    default: {
      return undefined;
    }
  }
};

const getSubTitleKeyGB = ({modalType}: GetSubTitleKeyProps) => {
  switch (modalType) {
    case 'split_pay':
    case 'adaptive': {
      return 'paymentTerms.splitPayContents.subtitle.splitPayOnly3';
    }
    case 'interest_only': {
      return 'paymentTerms.splitPayContents.subtitle.informational';
    }
    default: {
      return undefined;
    }
  }
};

export interface GetLegalCopyKeysProps {
  countryCode?: string;
  lendersLink: JSX.Element;
  modalType: ModalType;
  loanTypes: AvailableLoanType[];
  showNewBuyerIncentive?: boolean;
}
export const getLegalCopyKeys = ({
  countryCode,
  lendersLink,
  modalType,
  loanTypes,
  showNewBuyerIncentive,
}: GetLegalCopyKeysProps) => {
  const affirmUrlKey: [string, TranslateOptions] = [
    'paymentTerms.splitPayContents.legal.ratesFromApr',
    {
      link: lendersLink,
    },
  ];

  switch (countryCode) {
    case 'CA': {
      return ['paymentTerms.splitPayContents.legal.CA'];
    }
    case 'US': {
      const newBuyerIncentiveKey = showNewBuyerIncentive
        ? ['paymentTerms.splitPayContents.legal.newBuyerIncentive']
        : [];

      switch (modalType) {
        case 'adaptive': {
          return [
            ...newBuyerIncentiveKey,
            'paymentTerms.splitPayContents.legal.interestAndSplitPay',
            affirmUrlKey,
            'paymentTerms.splitPayContents.legal.caResidentsNotice',
          ];
        }
        case 'split_pay': {
          return [
            ...newBuyerIncentiveKey,
            'paymentTerms.splitPayContents.legal.splitPayOnly',
            'paymentTerms.splitPayContents.legal.caResidentsNotice',
          ];
        }
        case 'interest_only': {
          return [
            ...newBuyerIncentiveKey,
            'paymentTerms.splitPayContents.legal.interestOnly',
            affirmUrlKey,
            'paymentTerms.splitPayContents.legal.caResidentsNotice',
          ];
        }
        case 'ineligible': {
          return [
            'paymentTerms.splitPayContents.legal.ineligible',
            'paymentTerms.splitPayContents.legal.caResidentsNotice',
          ];
        }
        default: {
          return [];
        }
      }
    }
    case 'GB': {
      // Below the program floor (i.e. below <£50)
      // Pi3 only
      if (
        loanTypes.length === 0 ||
        (loanTypes.length === 1 && loanTypes[0] === 'split_pay') ||
        (loanTypes.includes('split_pay') && loanTypes.includes('interest'))
      ) {
        return [
          'paymentTerms.splitPayContents.legal.topVariant1GB',
          'paymentTerms.splitPayContents.legal.bottomGB',
        ];
      }
      // Pi3 plus zero percent
      // Single zero percent terms
      // Multiple zero percent terms
      else if (
        loanTypes.includes('split_pay') ||
        loanTypes.includes('zero_percent')
      ) {
        return [
          'paymentTerms.splitPayContents.legal.topVariant2GB',
          'paymentTerms.splitPayContents.legal.bottomGB',
        ];
      }
      // Interest bearing loans only
      else {
        return [
          'paymentTerms.splitPayContents.legal.topVariant3GB',
          'paymentTerms.splitPayContents.legal.bottomGB',
        ];
      }
    }
    default: {
      return [];
    }
  }
};

type GetIneligibleSubtitleKeyProps = Pick<
  InstallmentsContextType,
  | 'minIneligibleMessageType'
  | 'numberOfPaymentTerms'
  | 'fullPrice'
  | 'countryCode'
> & {
  priceRange?: {
    minPrice: string;
    maxPrice: string;
  };
};
const getIneligibleSubtitleKey = ({
  countryCode,
  minIneligibleMessageType,
  numberOfPaymentTerms,
  fullPrice,
  priceRange,
}: GetIneligibleSubtitleKeyProps): string => {
  const minPriceNumber = priceRange?.minPrice
    ? convertPriceToNumber(priceRange.minPrice)
    : null;
  const priceNumber = fullPrice ? convertPriceToNumber(fullPrice) : null;

  if (!minPriceNumber || !priceRange?.maxPrice) {
    return '';
  }

  if (!priceNumber || priceNumber < minPriceNumber) {
    let translationKey = 'paymentTerms.splitPayContents.subtitle.ineligibleMin';

    if (countryCode === 'GB') {
      return 'paymentTerms.splitPayContents.subtitle.ineligibleMinGB';
    }

    if (numberOfPaymentTerms === 2 || numberOfPaymentTerms === 1) {
      translationKey =
        'paymentTerms.splitPayContents.subtitle.ineligibleMinOverTime';
    }

    const ineligibleMonthlySubtitleKey =
      'paymentTerms.splitPayContents.subtitle.ineligibleMonthlyPaymentsMin';

    return minIneligibleMessageType === 'monthly'
      ? ineligibleMonthlySubtitleKey
      : translationKey;
  }

  const subTitleKey = 'paymentTerms.splitPayContents.subtitle.ineligibleMax';

  return subTitleKey;
};
